/* eslint-disable @typescript-eslint/member-ordering */

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  viewChild, WritableSignal,
} from '@angular/core';
import { AlertComponent } from '@libs/common/components';
import {
  CartItemPayloadModel,
  ProductListPayloadModel,
  QuickOrderAddTab,
  QuickOrderSelectionModel,
  ShoppingListItemModel,
  ShoppingListModel,
} from '@libs/shop/models';
import { CartService, QuickOrderService, ShoppingListsState } from '@libs/shared/services';
import { CartTrackingService, DownloadTrackingService, ProductTrackingListDirective } from '@libs/shared/tracking';
import { firstValueFrom } from 'rxjs';
import { QuickOrderInitialComponent } from '../quick-order-initial/quick-order-initial.component';
import { QuickOrderProductSelectionComponent } from '../quick-order-product-selection/quick-order-product-selection.component';
import { ShoppingListItemsComponent } from '../../shopping-list/shopping-list-items/shopping-list-items.component';

@Component({
  selector: 'ui-quick-order-product-list',
  imports: [ShoppingListItemsComponent, AlertComponent, QuickOrderInitialComponent, NgTemplateOutlet, QuickOrderProductSelectionComponent],
  templateUrl: './quick-order-product-list.component.html',
  styleUrl: './quick-order-product-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickOrderProductListComponent implements OnInit {
  private readonly shoppingListState: ShoppingListsState = inject(ShoppingListsState);
  private readonly cartService: CartService = inject(CartService);
  private readonly qoService: QuickOrderService = inject(QuickOrderService);
  private readonly downloadTracking: DownloadTrackingService = inject(DownloadTrackingService);
  private readonly cartTrackingService: CartTrackingService = inject(CartTrackingService);
  private readonly productTrackingList: ProductTrackingListDirective | null = inject(ProductTrackingListDirective, { optional: true });

  public productsToSelect: ModelSignal<QuickOrderSelectionModel[]> = model.required();
  public missingProducts: InputSignal<string[]> = input.required();
  public quickOrderListId: Signal<string> = computed(() => this.qoService.determineQuickOrderShoppingListId());
  public readonly tabSelected: OutputEmitterRef<QuickOrderAddTab> = output();

  public quickOrderItemList: Signal<ShoppingListModel> = computed(() => this.shoppingListState.entityMap()[this.quickOrderListId()]);
  public error: Signal<Error | undefined> = computed(() => this.shoppingListState.error() ?? this.shoppingListState.loadShoppingListByIdError());
  public isLoading: Signal<boolean> = computed(() => this.shoppingListState.isLoading() || this.shoppingListState.loadShoppingListByIdIsPending());

  public items: Signal<ShoppingListItemModel[]> = computed(
    () => this.quickOrderItemList().items,
  );

  public missingProductsAlertMessage: Signal<string> = computed(() => `Folgende Artikel konnten nicht eindeutig zugeordnet werden: ${this.missingProducts().join(',')}`);

  public isDownloading: WritableSignal<boolean> = signal(false);

  private readonly shoppingListItemsComponent: Signal<ShoppingListItemsComponent> = viewChild.required(ShoppingListItemsComponent);

  public ngOnInit(): void {
    void this.shoppingListState.loadShoppingListById(this.quickOrderListId());
  }

  public async onItemQuantityChanged(payload: ShoppingListItemModel): Promise<void> {
    await this.shoppingListState.updateProductInShoppingList({ listId: this.quickOrderListId(), item: payload });
  }

  public async onRemoveItems(payload: ShoppingListItemModel | ShoppingListItemModel[]): Promise<void> {
    if (Array.isArray(payload)) {
      await this.shoppingListState.deleteProductsFromShoppingListCollection({ listId: this.quickOrderListId(), items: payload });
      this.shoppingListItemsComponent().clearOrCloseSelection();

      return;
    }

    await this.shoppingListState.deleteProductFromShoppingList({ listId: this.quickOrderListId(), item: payload });
  }

  public onItemsAddToCart(payload: ShoppingListItemModel[]): void {
    const collection: CartItemPayloadModel[] = payload.map((item) => {
      return {
        type: 'cart-item',
        id: item.sku,
        quantity: item.quantity,
      };
    });

    void firstValueFrom(this.cartService.addItemsCollection(collection))
      .then((result) => {
        this.cartTrackingService.trackAddToCartListItems(payload, result.errors, this.productTrackingList?.trackingList());
        this.shoppingListItemsComponent().clearOrCloseSelection();
        void this.shoppingListState.deleteProductsFromShoppingListCollectionSilent({ listId: this.quickOrderListId(), items: payload });
      });
  }

  public onItemsSelected(selection: ShoppingListItemModel[]): void {
    const payload: ProductListPayloadModel[] = selection.map(x => ({
      type: 'shopping-list-items',
      id: '',
      sku: x.sku,
      quantity: x.quantity,
    }));

    void this.shoppingListState.addProductCollectionToShoppingList({ listId: this.quickOrderListId(), collection: payload });
  }

  public download(): void {
    this.isDownloading.set(true);
    void this.shoppingListState.downloadShoppingList(this.quickOrderListId())
      .then((fileName: string) => void this.downloadTracking.trackDownload(fileName))
      .finally(() => void this.isDownloading.set(false));
  }
}
